import { FunctionComponent, useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { DashboardWrapper, Card, Text, Button } from 'src/shared';
import StatusBadge from 'src/shared/statusBadge';
import { Colors } from 'src/utils/color';
import { Size } from 'src/shared/text';
import { usePolling } from 'src/hooks/usePolling';
import { getAnalysisJobs, retryJob, reviewJob } from 'src/services/api';
import AssignPatientModal from 'src/shared/assignPatientModal';
import type { AssignPatientMode } from 'src/shared/assignPatientModal';
import type { AnalysisJob, DisplayStatus } from 'src/types/job';
import { toDisplayStatus, jobNeedsSubjectLink, jobIsSubjectLinked } from 'src/types/job';

// ---------------------------------------------------------------------------
// Filter types
// ---------------------------------------------------------------------------
type FilterKey = 'all' | 'in_progress' | 'completed' | 'failed' | 'needs_review';

const FILTERS: { key: FilterKey; label: string }[] = [
    { key: 'all',          label: 'All' },
    { key: 'in_progress',  label: 'In Progress' },
    { key: 'completed',    label: 'Completed' },
    { key: 'failed',       label: 'Failed' },
    { key: 'needs_review', label: 'Needs Review' },
];

function filterToApiParams(f: FilterKey): { status?: string; reviewed?: string } {
    switch (f) {
        case 'in_progress':  return { status: 'IN_PROGRESS' };
        case 'completed':    return { status: 'SUCCEEDED', reviewed: 'true' };
        case 'failed':       return { status: 'FAILED' };
        case 'needs_review': return { status: 'SUCCEEDED', reviewed: 'false' };
        default:             return {};
    }
}

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------
const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 24px;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
`;

const SubTitle = styled(Text)`
    color: ${Colors.extra.dark_gray};
`;

const MetricsRow = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 12px;
`;

const MetricCard = styled(Card)`
    padding: 16px;
`;

const MetricValue = styled.div`
    font-size: 28px;
    font-weight: 700;
    margin-bottom: 4px;
`;

const MetricLabel = styled.div`
    font-size: 13px;
    color: ${Colors.extra.dark_gray};
`;

const Toolbar = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
`;

const Pill = styled.button<{ $active?: boolean }>`
    border: 1px solid ${Colors.theme.sidebar_border};
    background: ${p => (p.$active ? 'rgba(37,125,230,0.12)' : Colors.extra.white)};
    color: ${p => (p.$active ? Colors.theme.primary : Colors.extra.black)};
    font-weight: ${p => (p.$active ? 600 : 400)};
    padding: 8px 14px;
    border-radius: 999px;
    cursor: pointer;
    font-size: 13px;
    transition: all 0.15s ease;
    &:hover { background: rgba(37,125,230,0.08); }
`;

const Table = styled.div`
    display: flex;
    flex-direction: column;
    border: 1px solid ${Colors.extra.light_gray};
    border-radius: 10px;
    overflow: hidden;
`;

const TableHeader = styled.div`
    display: grid;
    grid-template-columns: 2fr 1fr 140px 180px;
    padding: 12px 16px;
    background: ${Colors.extra.light_gray};
    font-size: 12px;
    font-weight: 600;
    color: ${Colors.extra.dark_gray};
    text-transform: uppercase;
    letter-spacing: 0.03em;
`;

const TableRow = styled.div`
    display: grid;
    grid-template-columns: 2fr 1fr 140px 180px;
    padding: 14px 16px;
    align-items: center;
    border-top: 1px solid ${Colors.extra.light_gray};
    transition: background 0.1s ease;
    &:hover { background: ${Colors.extra.light_gray}55; }
`;

const CellMain = styled.div`
    font-weight: 500;
    color: ${Colors.extra.black};
    font-size: 14px;
`;

const CellSub = styled.div`
    font-size: 12px;
    color: ${Colors.extra.dark_gray};
    margin-top: 2px;
`;

const Actions = styled.div`
    display: flex;
    gap: 8px;
`;

const SmallButton = styled.button<{ $variant?: 'primary' | 'danger' | 'ghost' }>`
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    border: 1px solid transparent;
    transition: all 0.15s ease;
    ${p => {
        if (p.$variant === 'primary') return `background:${Colors.theme.primary};color:#fff;&:hover{opacity:0.9;}`;
        if (p.$variant === 'danger') return `background:#FEE2E2;color:#DC2626;border-color:#FECACA;&:hover{background:#FECACA;}`;
        return `background:transparent;color:${Colors.extra.dark_gray};border-color:${Colors.extra.light_gray};&:hover{background:${Colors.extra.light_gray};}`;
    }}
`;

const spin = keyframes`from{transform:rotate(0deg);}to{transform:rotate(360deg);}`;
const Spinner = styled.span`
    display: inline-block;
    width: 14px; height: 14px;
    border: 2px solid ${Colors.theme.primary}33;
    border-top-color: ${Colors.theme.primary};
    border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
    vertical-align: middle;
    margin-right: 6px;
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 64px 24px;
    text-align: center;
`;

const Workflow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 8px;
`;

const Step = styled.div`
    padding: 8px 14px;
    border-radius: 8px;
    background: ${Colors.extra.light_gray};
    font-size: 13px;
    font-weight: 500;
`;

const Arrow = styled.span`
    color: ${Colors.extra.dark_gray};
    font-size: 18px;
`;

const LinkBadge = styled.span<{ $variant: 'needs' | 'linked' }>`
    font-size: 10px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 999px;
    margin-left: 6px;
    vertical-align: middle;
    ${p =>
        p.$variant === 'needs'
            ? `color: #B45309; background: #FEF3C7;`
            : `color: #047857; background: #D1FAE5;`}
`;

const PaginationRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const SearchInput = styled.input`
    height: 36px;
    border: 1px solid ${Colors.theme.sidebar_border};
    border-radius: 8px;
    padding: 0 12px;
    outline: none;
    font-size: 13px;
    min-width: 200px;
    background: ${Colors.extra.white};
    color: ${Colors.extra.black};
    &:focus { border-color: ${Colors.theme.primary}; box-shadow: 0 0 0 3px rgba(37,125,230,0.1); }
`;

function formatPatientLabel(job: AnalysisJob): string | null {
    const bare = job.patient_label ?? job.patient_name;
    if (!bare) return null;
    if (job.patient_is_session === true) return `Session: ${bare}`;
    return `Subject: ${bare}`;
}

const ScansPage: FunctionComponent = () => {
    const navigate = useNavigate();
    const [filter, setFilter] = useState<FilterKey>('all');
    const [jobs, setJobs] = useState<AnalysisJob[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [counts, setCounts] = useState({ all: 0, in_progress: 0, completed: 0, failed: 0, needs_review: 0 });
    const initialLoadDone = useRef(false);
    const [selectedJobs, setSelectedJobs] = useState<Set<string>>(new Set());
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [assignModalMode, setAssignModalMode] = useState<AssignPatientMode>('link');
    const [bulkSubjectSummary, setBulkSubjectSummary] = useState<string | undefined>();

    const openBulkAssign = useCallback(() => {
        const selectedList = jobs.filter(j => selectedJobs.has(j.id));
        if (selectedList.length === 0) return;
        const hasLinked = selectedList.some(j => jobIsSubjectLinked(j));
        const needsLink = selectedList.some(j => jobNeedsSubjectLink(j));
        if (hasLinked && needsLink) {
            window.alert('Some scans already have assigned subjects. Use Reassign.');
            return;
        }
        if (hasLinked) {
            setAssignModalMode('reassign');
            const first = selectedList[0];
            const pid = first.patient_id ?? first.patient;
            const same = selectedList.every(j => (j.patient_id ?? j.patient) === pid);
            const lbl = first.patient_label ?? first.patient_name;
            if (same && lbl) {
                setBulkSubjectSummary(`${selectedList.length} scans · Subject: ${lbl}`);
            } else {
                setBulkSubjectSummary(`${selectedList.length} scans linked to subjects`);
            }
        } else {
            setAssignModalMode('link');
            setBulkSubjectSummary(undefined);
        }
        setShowAssignModal(true);
    }, [jobs, selectedJobs]);

    const pageSize = 20;

    const loadJobs = useCallback(async () => {
        if (!initialLoadDone.current) {
            setLoading(true);
        }
        try {
            const apiParams = filterToApiParams(filter);
            const res = await getAnalysisJobs({ page, page_size: pageSize, ...apiParams, search: searchText || undefined });
            const results = res?.results ?? res?.data ?? [];
            setJobs(results);
            setTotalCount(res?.count ?? results.length);
        } catch {
            setJobs([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
            initialLoadDone.current = true;
        }
    }, [filter, page, searchText]);

    // Also fetch unfiltered count once for metrics (lightweight)
    const loadCounts = useCallback(async () => {
        try {
            const [all, prog, comp, fail, rev] = await Promise.all([
                getAnalysisJobs({ page: 1, page_size: 1 }),
                getAnalysisJobs({ page: 1, page_size: 1, status: 'IN_PROGRESS' }),
                getAnalysisJobs({ page: 1, page_size: 1, status: 'SUCCEEDED', reviewed: 'true' }),
                getAnalysisJobs({ page: 1, page_size: 1, status: 'FAILED' }),
                getAnalysisJobs({ page: 1, page_size: 1, status: 'SUCCEEDED', reviewed: 'false' }),
            ]);
            setCounts({
                all: all?.count ?? 0,
                in_progress: prog?.count ?? 0,
                completed: comp?.count ?? 0,
                failed: fail?.count ?? 0,
                needs_review: rev?.count ?? 0,
            });
        } catch { /* non-critical */ }
    }, []);

    usePolling(() => { loadJobs(); loadCounts(); }, 12_000);

    // Trigger immediate fetch when filter, page, or search changes
    useEffect(() => { loadJobs(); }, [loadJobs]);

    const handleRetry = async (jobId: string) => {
        try {
            await retryJob(jobId);
            loadJobs();
            loadCounts();
        } catch { /* toast later */ }
    };

    const handleReview = async (jobId: string) => {
        try {
            await reviewJob(jobId);
            loadJobs();
            loadCounts();
        } catch { /* toast later */ }
    };

    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

    const metricCards: { key: FilterKey; label: string; value: number; color: string }[] = [
        { key: 'all',          label: 'Total Scans',    value: counts.all,          color: Colors.extra.black },
        { key: 'in_progress',  label: 'In Progress',    value: counts.in_progress,  color: '#2563EB' },
        { key: 'completed',    label: 'Completed',      value: counts.completed,    color: '#059669' },
        { key: 'failed',       label: 'Failed',         value: counts.failed,       color: '#DC2626' },
        { key: 'needs_review', label: 'Needs Review',   value: counts.needs_review, color: '#D97706' },
    ];

    return (
        <DashboardWrapper>
            <Container>
                <Header>
                    <div>
                        <Text fontSize={Size.Large} fontWeight="600" color={Colors.extra.black}>
                            Scan Analysis
                        </Text>
                        <SubTitle fontSize={Size.Small}>
                            Track scan processing across the analysis pipeline
                        </SubTitle>
                    </div>
                    <Button onClick={() => navigate('/retina/upload')}>Upload Scan</Button>
                </Header>

                {/* Metric cards — always show totals, not affected by filter */}
                <MetricsRow>
                    {metricCards.map(m => (
                        <MetricCard key={m.key}>
                            <MetricValue style={{ color: m.color }}>{m.value}</MetricValue>
                            <MetricLabel>{m.label}</MetricLabel>
                        </MetricCard>
                    ))}
                </MetricsRow>

                {/* Search + filter pills */}
                <Toolbar>
                    <SearchInput
                        placeholder="Search by scan ID, patient name…"
                        value={searchText}
                        onChange={e => { setSearchText(e.target.value); setPage(1); }}
                    />
                    {FILTERS.map(f => (
                        <Pill key={f.key} $active={filter === f.key} onClick={() => { setFilter(f.key); setPage(1); }}>
                            {f.label}
                        </Pill>
                    ))}
                </Toolbar>

                {/* Bulk action bar */}
                {selectedJobs.size > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0' }}>
                        <Text fontSize={Size.XSmall} fontWeight="600">{selectedJobs.size} selected</Text>
                        <SmallButton $variant="primary" onClick={openBulkAssign}>
                            Assign to Patient
                        </SmallButton>
                        <SmallButton $variant="ghost" onClick={() => setSelectedJobs(new Set())}>
                            Clear
                        </SmallButton>
                    </div>
                )}

                {showAssignModal && selectedJobs.size > 0 && (
                    <AssignPatientModal
                        jobIds={Array.from(selectedJobs)}
                        mode={assignModalMode}
                        currentSubjectSummary={bulkSubjectSummary}
                        onClose={() => {
                            setShowAssignModal(false);
                            setBulkSubjectSummary(undefined);
                        }}
                        onAssigned={() => { setSelectedJobs(new Set()); loadJobs(); loadCounts(); }}
                    />
                )}

                {/* Scan table */}
                {loading ? (
                    <div style={{ padding: 32, textAlign: 'center' }}>
                        <Spinner /><Text>Loading scans…</Text>
                    </div>
                ) : jobs.length === 0 ? (
                    <EmptyState>
                        <Text fontSize={Size.Medium} fontWeight="600">No scans found</Text>
                        <SubTitle fontSize={Size.Small}>Upload a scan to begin processing</SubTitle>
                        <Button onClick={() => navigate('/retina')}>Upload Your First Scan</Button>
                        <Workflow>
                            <Step>Upload scan</Step>
                            <Arrow>&rarr;</Arrow>
                            <Step>Processing</Step>
                            <Arrow>&rarr;</Arrow>
                            <Step>Review results</Step>
                        </Workflow>
                    </EmptyState>
                ) : (
                    <>
                        <Table>
                            <TableHeader>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <input
                                        type="checkbox"
                                        checked={jobs.length > 0 && jobs.every(j => selectedJobs.has(j.id))}
                                        onChange={e => {
                                            if (e.target.checked) setSelectedJobs(new Set(jobs.map(j => j.id)));
                                            else setSelectedJobs(new Set());
                                        }}
                                    />
                                    Scan
                                </div>
                                <div>Submitted</div>
                                <div>Status</div>
                                <div>Actions</div>
                            </TableHeader>
                            {jobs.map(job => {
                                const display: DisplayStatus = toDisplayStatus(job);
                                const shortUri = job.input_data_uri
                                    ? job.input_data_uri.split('/').pop() ?? job.input_data_uri
                                    : '—';
                                return (
                                    <TableRow key={job.id}>
                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                            <input
                                                type="checkbox"
                                                checked={selectedJobs.has(job.id)}
                                                onChange={e => {
                                                    const next = new Set(selectedJobs);
                                                    if (e.target.checked) next.add(job.id); else next.delete(job.id);
                                                    setSelectedJobs(next);
                                                }}
                                                style={{ marginTop: 3 }}
                                            />
                                            <div>
                                            <CellMain>
                                                {shortUri}
                                                {jobNeedsSubjectLink(job) && (
                                                    <LinkBadge $variant="needs">Needs subject</LinkBadge>
                                                )}
                                                {jobIsSubjectLinked(job) && (
                                                    <LinkBadge $variant="linked">Linked</LinkBadge>
                                                )}
                                            </CellMain>
                                            <CellSub>
                                                {formatPatientLabel(job)
                                                    ? `${formatPatientLabel(job)} · `
                                                    : ''}
                                                by {job.initiated_by_username}
                                                {job.parent_job_id ? ' (retry)' : ''}
                                            </CellSub>
                                            </div>
                                        </div>
                                        <div>
                                            <CellMain>
                                                {new Date(job.created_at).toLocaleDateString()}
                                            </CellMain>
                                            <CellSub>
                                                {new Date(job.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                {job.reviewed && job.reviewed_by_username ? ` · Reviewed by ${job.reviewed_by_username}` : ''}
                                            </CellSub>
                                        </div>
                                        <div>
                                            {(display === 'Processing') && <Spinner />}
                                            <StatusBadge status={display} />
                                        </div>
                                        <Actions>
                                            {job.status === 'SUCCEEDED' && (
                                                <SmallButton
                                                    $variant="primary"
                                                    onClick={() => navigate(`/scans/${job.id}/result`)}
                                                >
                                                    View Result
                                                </SmallButton>
                                            )}
                                            {job.status === 'SUCCEEDED' && !job.reviewed && (
                                                <SmallButton $variant="ghost" onClick={() => handleReview(job.id)}>
                                                    Mark Reviewed
                                                </SmallButton>
                                            )}
                                            {job.status === 'SUCCEEDED' && jobIsSubjectLinked(job) && (
                                                <SmallButton
                                                    $variant="ghost"
                                                    onClick={() => {
                                                        setSelectedJobs(new Set([job.id]));
                                                        setAssignModalMode('reassign');
                                                        setBulkSubjectSummary(formatPatientLabel(job) || undefined);
                                                        setShowAssignModal(true);
                                                    }}
                                                >
                                                    Reassign…
                                                </SmallButton>
                                            )}
                                            {job.status === 'FAILED' && (
                                                <SmallButton $variant="danger" onClick={() => handleRetry(job.id)}>
                                                    Retry
                                                </SmallButton>
                                            )}
                                        </Actions>
                                    </TableRow>
                                );
                            })}
                        </Table>

                        <PaginationRow>
                            <SubTitle fontSize={Size.XSmall}>
                                Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalCount)} of {totalCount}
                            </SubTitle>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <SmallButton $variant="ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                                    Previous
                                </SmallButton>
                                <SmallButton $variant="ghost" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                                    Next
                                </SmallButton>
                            </div>
                        </PaginationRow>
                    </>
                )}
            </Container>
        </DashboardWrapper>
    );
};

export default ScansPage;
